import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Paperclip, Loader2, Trash2, ExternalLink, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../src/contexts/AuthContext';
import { useToast } from '../../src/contexts/ToastContext';
import {
    formatarTamanhoArquivo, conferirDocumentos, lerModoDocumentos,
    listarDocumentosDaSolicitacao, enviarDocumentoCancelamento, linkDocumentoCancelamento,
    apagarDocumentoCancelamento, motivoArquivoRecusado, descreverSolicitante,
    LIMITE_ARQUIVOS_POR_PEDIDO,
} from '../../src/lib/documentosCancelamento';
import type { ModoDocumentos } from '../../src/lib/documentosCancelamento';

interface Props {
    solicitacaoId: number;
    solicitanteNome?: string | null;
    solicitanteVinculo?: string | null;
    solicitanteContato?: string | null;
}

interface DocumentoLinha {
    id: number;
    caminho: string;
    nome_original: string;
    tamanho_bytes: number | null;
    enviado_no_portal: boolean;
    created_at: string;
}

const fmtTamanho = formatarTamanhoArquivo;

/**
 * Documentos comprobatórios do pedido (#5828), na aba Cancelamentos.
 *
 * O arquivo vive em bucket privado: abrir exige link assinado, e é por isso que
 * o botão chama `linkDocumentoCancelamento` em vez de usar uma URL direta.
 * Apagar é só adm e gerente — a RLS recusa os demais, e a tela nem oferece.
 */
const DocumentosCancelamento: React.FC<Props> = ({ solicitacaoId, solicitanteNome, solicitanteVinculo, solicitanteContato }) => {
    const { user, profile } = useAuth();
    const { addToast } = useToast();
    const inputRef = useRef<HTMLInputElement>(null);

    const [docs, setDocs] = useState<DocumentoLinha[]>([]);
    const [carregando, setCarregando] = useState(true);
    const [enviando, setEnviando] = useState(false);
    const [apagando, setApagando] = useState<number | null>(null);

    const podeApagar = profile?.role === 'adm' || profile?.role === 'gerente';

    const carregar = useCallback(async () => {
        setCarregando(true);
        try {
            setDocs(await listarDocumentosDaSolicitacao(solicitacaoId) as DocumentoLinha[]);
        } catch (e: any) {
            console.error('Erro ao listar documentos do cancelamento', e);
            addToast('Não foi possível carregar os documentos.', 'error');
        } finally {
            setCarregando(false);
        }
    }, [solicitacaoId, addToast]);

    useEffect(() => { carregar(); }, [carregar]);

    const enviar = async (lista: FileList | null) => {
        const arquivos = Array.from(lista || []);
        if (arquivos.length === 0 || !user?.id) return;
        setEnviando(true);
        try {
            for (const [i, arquivo] of arquivos.entries()) {
                const recusa = motivoArquivoRecusado(arquivo, docs.length + i);
                if (recusa) { addToast(recusa, 'warning'); break; }
                await enviarDocumentoCancelamento(solicitacaoId, user.id, arquivo, { peloPortal: false, jaEnviados: docs.length + i });
            }
            await carregar();
        } catch (e: any) {
            console.error('Erro ao anexar documento do cancelamento', e);
            addToast('Erro ao anexar: ' + (e.message || ''), 'error');
        } finally {
            setEnviando(false);
        }
    };

    const abrir = async (caminho: string) => {
        try {
            window.open(await linkDocumentoCancelamento(caminho), '_blank', 'noopener');
        } catch (e: any) {
            addToast(e.message || 'Não foi possível abrir o documento.', 'error');
        }
    };

    const apagar = async (doc: DocumentoLinha) => {
        setApagando(doc.id);
        try {
            await apagarDocumentoCancelamento(doc.id, doc.caminho);
            addToast('Documento removido.', 'success');
            await carregar();
        } catch (e: any) {
            addToast('Erro ao remover: ' + (e.message || ''), 'error');
        } finally {
            setApagando(null);
        }
    };

    const quemPediu = descreverSolicitante(solicitanteNome, solicitanteVinculo);
    // O que este perfil precisa anexar (#5828). O modo vem da configuração:
    // 'aviso' (padrão de hoje) mostra a atenção sem impedir nada; vira
    // 'bloqueio' no dia em que a diretoria aprovar a lista definitiva.
    const [modoDocs, setModoDocs] = useState<ModoDocumentos>('aviso');
    useEffect(() => { lerModoDocumentos().then(setModoDocs).catch(() => setModoDocs('aviso')); }, []);
    const conferencia = carregando ? null : conferirDocumentos(docs, solicitanteVinculo, modoDocs);

    return (
        <div>
            <h3 className="text-sm font-bold text-gray-800 dark:text-slate-200 border-b border-gray-100 dark:border-slate-700 pb-2 mb-1 flex items-center gap-2">
                <Paperclip size={15} className="text-gray-400 dark:text-slate-500" aria-hidden="true" /> Solicitante e documentos
            </h3>

            <p className="text-[11px] text-gray-400 dark:text-slate-500 mb-3">
                {quemPediu
                    ? <>Pedido feito por <span className="font-semibold text-gray-600 dark:text-slate-300">{quemPediu}</span>{solicitanteContato ? ` · ${solicitanteContato}` : ''}</>
                    : 'Este pedido não registrou quem solicitou — é anterior ao campo ou veio por outro canal.'}
            </p>

            {carregando ? (
                <p className="text-xs text-gray-400 dark:text-slate-500 py-3">
                    <Loader2 size={13} className="animate-spin inline mr-1.5" /> Carregando documentos...
                </p>
            ) : docs.length === 0 ? (
                <div className="rounded-xl border border-amber-200 dark:border-amber-800/50 bg-amber-50/70 dark:bg-amber-900/15 px-3.5 py-3 flex items-start gap-3">
                    <AlertTriangle size={15} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" aria-hidden="true" />
                    <div className="text-xs text-gray-700 dark:text-slate-300">
                        <p>Nenhum documento anexado. Peça ao cliente ou anexe aqui o que comprova o pedido.</p>
                        {conferencia && conferencia.faltando.length > 0 && (
                            <p className="mt-1 text-[11px] text-gray-600 dark:text-slate-400">{conferencia.texto}</p>
                        )}
                    </div>
                </div>
            ) : (
                <ul className="flex flex-col gap-1.5">
                    {docs.map(d => (
                        <li key={d.id} className="flex items-center justify-between gap-2 rounded-xl border border-gray-100 dark:border-slate-700 bg-gray-50/60 dark:bg-slate-800/50 px-3.5 py-2">
                            <div className="min-w-0">
                                <p className="text-sm text-gray-800 dark:text-slate-100 truncate">{d.nome_original}</p>
                                <p className="text-[11px] text-gray-400 dark:text-slate-500">
                                    {d.enviado_no_portal ? 'enviado pelo cliente' : 'anexado pela equipe'}
                                    {fmtTamanho(d.tamanho_bytes) ? ` · ${fmtTamanho(d.tamanho_bytes)}` : ''}
                                    {` · ${new Date(d.created_at).toLocaleDateString('pt-BR')}`}
                                </p>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                                <button onClick={() => abrir(d.caminho)} aria-label={`Abrir ${d.nome_original}`} title="Abrir documento"
                                    className="p-1.5 rounded-md text-gray-400 dark:text-slate-500 hover:text-primary hover:bg-primary/10 transition-colors">
                                    <ExternalLink size={14} />
                                </button>
                                {podeApagar && (
                                    <button onClick={() => apagar(d)} disabled={apagando === d.id}
                                        aria-label={`Remover ${d.nome_original}`} title="Remover documento"
                                        className="p-1.5 rounded-md text-gray-400 dark:text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50">
                                        {apagando === d.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                                    </button>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            <div className="mt-2.5">
                <button type="button" onClick={() => inputRef.current?.click()}
                    disabled={enviando || docs.length >= LIMITE_ARQUIVOS_POR_PEDIDO}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-primary hover:opacity-80 disabled:opacity-50">
                    {enviando ? <Loader2 size={12} className="animate-spin" /> : <Paperclip size={12} />}
                    {enviando ? 'Enviando...' : 'Anexar documento'}
                </button>
                <input ref={inputRef} type="file" multiple className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png,.heic,.heif"
                    onChange={e => { enviar(e.target.files); e.target.value = ''; }} />
                {docs.length >= LIMITE_ARQUIVOS_POR_PEDIDO && (
                    <span className="ml-2 text-[11px] text-gray-400 dark:text-slate-500">
                        limite de {LIMITE_ARQUIVOS_POR_PEDIDO} documentos atingido
                    </span>
                )}
            </div>
        </div>
    );
};

export default DocumentosCancelamento;
